import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAdminGroupsQuery } from '../../../api/group/groupAdminApiSlice';
import type { OrganizationRoomGrant } from '../../../api/organization/organizationApiTypes';
import GrantWindowsEditor from './GrantWindowsEditor';
import useSeriesSeason from './useSeriesSeason';
import { windowEditorOffered } from './windowEditorOffered';

interface OrgAllocationWindowsProps {
    organizationId: string;
    grants: OrganizationRoomGrant[];
}

/**
 * The organisation's allocation windows, one unit per room whose access
 * carries windows (`windowEditorOffered`): every shared room, and an open
 * ("public") one of a use case with allocation windows (the sports hall).
 * A dedicated room is theirs all the time, so it has no windows (the API
 * refuses them, `grant_dedicated`).
 */
const OrgAllocationWindows: React.FC<OrgAllocationWindowsProps> = ({ organizationId, grants }) => {
    const { t } = useTranslation();
    const season = useSeriesSeason();
    // The rooms' use cases: open access gets windows only on a hall.
    const { data: groups } = useGetAdminGroupsQuery();
    const roomUseCases = (groupId: string) => groups?.find((group) => group.id === groupId)?.compartmentTypes;
    const windowed = grants.filter((grant) => windowEditorOffered(grant.accessMode, roomUseCases(grant.groupId)));
    if (windowed.length === 0) return null;
    return (
        <>
            {windowed.map((grant) => (
                <GrantWindowsEditor
                    key={grant.id}
                    organizationId={organizationId}
                    grantId={grant.id}
                    title={t('allocation_windows.editor.title_room', {
                        room: grant.groupName?.trim() || t('common.not_applicable'),
                    })}
                    season={season}
                />
            ))}
        </>
    );
};

export default OrgAllocationWindows;
